import type { PartnershipItem } from '@/types/models/Partnership';
import type { WhitelabelConfigValue } from '@/types/models/WhitelabelConfig';

import { isNonEmptyString } from './validators/strings';

type RawPartnershipValue = WhitelabelConfigValue | null | undefined;

type RawPartnershipItem = {
  name?: RawPartnershipValue;
  logo?: RawPartnershipValue;
  link?: RawPartnershipValue;
};

const isRawPartnershipList = (
  value: RawPartnershipValue,
): value is RawPartnershipValue[] => Array.isArray(value);

const isRawPartnershipItem = (value: RawPartnershipValue): value is RawPartnershipItem =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isSafeHttpUrl = (value: string): boolean => {
  try {
    const url = new URL(value);

    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const toPartnershipItem = (raw: RawPartnershipValue): PartnershipItem | null => {
  if (!isRawPartnershipItem(raw)) {
    return null;
  }

  const { name, logo, link } = raw;

  if (!isNonEmptyString(name) || !isNonEmptyString(logo)) {
    return null;
  }

  if (!isSafeHttpUrl(logo)) {
    return null;
  }

  if (isNonEmptyString(link) && isSafeHttpUrl(link)) {
    return { name, logo, link };
  }

  return { name, logo };
};

export const parsePartnershipItems = (raw: RawPartnershipValue): PartnershipItem[] => {
  if (!isRawPartnershipList(raw)) {
    return [];
  }

  return raw.reduce<PartnershipItem[]>((items, entry) => {
    const item = toPartnershipItem(entry);

    if (item) {
      items.push(item);
    }

    return items;
  }, []);
};
